import { getStatusLabel } from "@/components/ResearchCms";
import type { EditorialContentDetail } from "@/api/researchCms.service";
import shared from "../cmsShared.module.css";
import styles from "./IndividualDetailsTab.module.css";

type RankingMetadata = {
  rankingTableId: string;
  rankingTableName: string | null;
  rankingId: string | null;
};

type IndividualDetailsTabProps = {
  content: EditorialContentDetail;
  ranking?: RankingMetadata;
  rankings?: RankingMetadata[];
};

const EMPTY_VALUE = "—";

function text(value: string | null): string {
  return value ?? EMPTY_VALUE;
}

function timestamp(value: string | null): string {
  return value ? new Date(value).toLocaleString() : EMPTY_VALUE;
}

export function IndividualDetailsTab({ content, ranking, rankings }: IndividualDetailsTabProps) {
  const metadataFields: [string, string][] = [
    ["Contact ID", text(content.contactId)],
    ["Company Name", text(content.firmName)],
    ["Content ID", content.id],
    ["Company ID", content.companyId],
    ["Office ID", text(content.officeId)],
    ["Directory ID", content.directoryId],
    ["Edition ID", content.editionId],
    ["Research BPA ID", content.researchBpaId],
    ["Parsed Document ID", text(content.docParseResultId)],
    ["Source Content ID", text(content.sourceContentId)],
    ["Source", getStatusLabel(content.source)],
    ["Review Status", getStatusLabel(content.reviewStatus)],
    ["Language", content.languageIso],
    ["Detected Language", text(content.detectedLanguage)],
    ["Reviewed By", text(content.reviewedBy)],
    ["Reviewed At", timestamp(content.reviewedAt)],
    ["Created By", text(content.createdBy)],
    ["Created At", timestamp(content.createdAt)],
    ["Updated By", text(content.updatedBy)],
    ["Updated At", timestamp(content.updatedAt)],
  ];

  if (ranking) {
    metadataFields.push(
      ["Ranking Table ID", ranking.rankingTableId],
      ["Ranking Table Name", text(ranking.rankingTableName)],
      ["Ranking ID", text(ranking.rankingId)],
    );
  }

  const allRankings = rankings ?? (ranking ? [ranking] : []);
  if (allRankings.length > 1) {
    for (let i = 0; i < allRankings.length; i++) {
      const r = allRankings[i];
      metadataFields.push(
        [`Ranking ${i + 1} — Table`, text(r.rankingTableName)],
        [`Ranking ${i + 1} — ID`, text(r.rankingId)],
      );
    }
  }

  const handleCopyAll = () => {
    const lines = metadataFields.map(([label, value]) => `${label}: ${value}`).join("\n");
    navigator.clipboard.writeText(lines);
  };

  return (
    <div className={shared.sectionCard}>
      <div className={styles.detailsHeader}>
        <h2 className={`${shared.sectionHeading} ${shared.headingInline}`}>Metadata</h2>
        <button type="button" className={styles.copyAllButton} onClick={handleCopyAll}>
          Copy All
        </button>
      </div>
      <div className={styles.detailsGrid}>
        {metadataFields.map(([label, value]) => (
          <div key={label} className={styles.detailRow}>
            <span className={styles.detailLabel}>{label}</span>
            <span className={styles.detailValue}>{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
